// frontend/src/components/ForcePasswordChange.js
import React, { useState } from 'react';
import { Eye, EyeOff, Lock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { buttonPrimary, focusRing } from '../theme';

const ForcePasswordChange = ({ onPasswordChanged }) => {
  const [passwords, setPasswords] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (passwords.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    if (passwords.newPassword !== passwords.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    try {
      setLoading(true);
      await axios.post('/api/auth/force-change-password', {
        newPassword: passwords.newPassword
      });
      
      toast.success('Password changed successfully!');
      onPasswordChanged();
    } catch (err) {
      console.error('Password change error:', err);
      toast.error(err.response?.data?.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-red-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
        <div className="flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mx-auto mb-4">
          <Lock size={32} className="text-orange-600" />
        </div>
        
        <h1 className="text-2xl font-bold text-gray-800 mb-2 text-center">Change Your Password</h1>
        <p className="text-sm text-gray-600 mb-6 text-center">
          For security reasons, you must change your password before continuing.
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              New Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={passwords.newPassword}
                onChange={(e) => setPasswords({...passwords, newPassword: e.target.value})}
                onKeyPress={handleKeyPress}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none ${focusRing}`}
                placeholder="Minimum 8 characters"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-2.5 text-gray-500"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Confirm Password
            </label>
            <input
              type={showPassword ? "text" : "password"}
              value={passwords.confirmPassword}
              onChange={(e) => setPasswords({...passwords, confirmPassword: e.target.value})}
              onKeyPress={handleKeyPress}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none ${focusRing}`}
              placeholder="Re-enter your password"
            />
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
            <p className="text-xs text-blue-800">
              <strong>Password requirements:</strong>
            </p>
            <ul className="text-xs text-blue-700 mt-1 list-disc list-inside">
              <li>At least 8 characters long</li>
              <li>Mix of letters and numbers recommended</li>
              <li>Avoid using common words</li>
            </ul>
          </div>

          <button
            onClick={handleSubmit}
            disabled={loading}
            className={`w-full py-2 rounded-md transition-colors disabled:bg-gray-400 ${buttonPrimary}`}
          >
            {loading ? 'Changing Password...' : 'Change Password'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ForcePasswordChange;